/**
 * The /admin/ai-usage date control and URL contract, as pure model code.
 *
 * ── Why days and not months ──────────────────────────────────────────────────
 * `admin_ai_usage` takes two instants and buckets them itself, so this page has
 * no month-shaped storage limit the way /admin/revenue does. Every preset here
 * resolves to a pair of INCLUSIVE LOCAL CALENDAR DAYS, and the RPC call turns
 * that pair into instants:
 *
 *   p_from = local midnight of the first day
 *   p_to   = local midnight of the day AFTER the last day (exclusive)
 *   p_tz   = the browser's IANA zone, the same zone those midnights came from
 *
 * Sending UTC midnights with a Sydney `p_tz` would put 10-11 hours of a
 * neighbouring day inside the range while the buckets were cut on Sydney days,
 * so the first and last bars would be wrong by that slice. The two must agree,
 * which is why the zone is read once here and passed with the instants.
 *
 * ── The bucket ceiling ───────────────────────────────────────────────────────
 * The RPC refuses more than 1000 buckets. A custom range of five years at day
 * grain asks for 1826, so the count is computed HERE and the page says "pick a
 * coarser grain" instead of firing a request that comes back as a 22023.
 */
import { addDaysYmd, formatDateRange, formatDateShort, formatDateWrittenShort, formatDayMonth, formatPeriodMonthYear, formatPeriodMonthYearLong } from '@/lib/dates';

// ---- presets -----------------------------------------------------------------

export type AiUsagePreset =
  | 'last7d'
  | 'last30d'
  | 'last90d'
  | 'this_month'
  | 'last_month'
  | 'custom';

/** The order the segmented control renders. `custom` is last: it is the escape hatch. */
export const AI_USAGE_PRESETS: readonly AiUsagePreset[] = [
  'last7d',
  'last30d',
  'last90d',
  'this_month',
  'last_month',
  'custom',
] as const;

export const AI_USAGE_PRESET_LABELS: Record<AiUsagePreset, string> = {
  last7d: 'Last 7 days',
  last30d: 'Last 30 days',
  last90d: 'Last 90 days',
  this_month: 'This month',
  last_month: 'Last month',
  custom: 'Custom',
};

export const DEFAULT_AI_USAGE_PRESET: AiUsagePreset = 'last30d';

export function isAiUsagePreset(value: unknown): value is AiUsagePreset {
  return typeof value === 'string' && (AI_USAGE_PRESETS as readonly string[]).includes(value);
}

// ---- grain -------------------------------------------------------------------

export type AiUsageGrain = 'day' | 'week' | 'month';

export const AI_USAGE_GRAINS: readonly AiUsageGrain[] = ['day', 'week', 'month'] as const;

export const AI_USAGE_GRAIN_LABELS: Record<AiUsageGrain, string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
};

export const DEFAULT_AI_USAGE_GRAIN: AiUsageGrain = 'day';

export function isAiUsageGrain(value: unknown): value is AiUsageGrain {
  return typeof value === 'string' && (AI_USAGE_GRAINS as readonly string[]).includes(value);
}

/** The RPC's own ceiling (contract §3). Exceeding it is a 22023, not a truncation. */
export const MAX_AI_USAGE_BUCKETS = 1000;

// ---- the URL contract --------------------------------------------------------

/**
 * URL-persisted state. Every field is optional and every default is omitted, so
 * the bare `/admin/ai-usage` path is the default view and any other view is a
 * shareable link.
 */
export interface AiUsageSearch {
  /** Omitted for the default preset (`last30d`). `custom` always carries from + to. */
  range?: AiUsagePreset;
  /** `YYYY-MM-DD`, inclusive. Only meaningful with `range=custom`. */
  from?: string;
  to?: string;
  /** Omitted for the default grain (`day`). */
  grain?: AiUsageGrain;
  /** Scope to one organization (`organizations.id`). */
  org?: string;
  /** Scope to one user. With `org` set too, the user wins for the totals. */
  user?: string;
}

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** `YYYY-MM-DD` and a real calendar day. */
export function isYmd(value: unknown): value is string {
  return typeof value === 'string' && YMD_RE.test(value) &&
    !Number.isNaN(Date.parse(`${value}T00:00:00Z`));
}

function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

/**
 * Parse raw URL search into a canonical `AiUsageSearch`.
 *
 * A half-written custom range (one bound, or `to` before `from`) is NOT a range:
 * it falls back to the default preset rather than querying something nobody
 * asked for. A bare `?from=…&to=…` with no `range` is read as a custom range,
 * which is what a hand-edited URL means.
 */
export function parseAiUsageSearch(search: Record<string, unknown>): AiUsageSearch {
  const out: AiUsageSearch = {};

  const range = isAiUsagePreset(search.range) ? search.range : undefined;
  const from = isYmd(search.from) ? search.from : undefined;
  const to = isYmd(search.to) ? search.to : undefined;
  const customUsable = from != null && to != null && from <= to;

  if (range === 'custom' || (range == null && customUsable)) {
    if (customUsable) {
      out.range = 'custom';
      out.from = from;
      out.to = to;
    }
  } else if (range != null && range !== DEFAULT_AI_USAGE_PRESET) {
    out.range = range;
  }

  if (isAiUsageGrain(search.grain) && search.grain !== DEFAULT_AI_USAGE_GRAIN) {
    out.grain = search.grain;
  }
  if (isUuid(search.org)) out.org = search.org;
  if (isUuid(search.user)) out.user = search.user;

  return out;
}

/**
 * Normalise a patched search back into the minimal object the URL should carry.
 * Every navigate on the page goes through this, so a default can never end up
 * spelled out in the querystring.
 */
export function serializeAiUsageSearch(s: AiUsageSearch): AiUsageSearch {
  return parseAiUsageSearch(s as Record<string, unknown>);
}

// ---- resolving a preset to days ----------------------------------------------

export interface ResolvedAiUsageRange {
  preset: AiUsagePreset;
  /** Inclusive local calendar days, `YYYY-MM-DD`. */
  fromYmd: string;
  toYmd: string;
}

/** Today in the BROWSER's zone - the same zone the instants below are built in. */
export function todayYmd(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Resolve the URL search to a day pair. `now` is injected so one render uses one
 * clock for every derived figure, and so this is testable without mocking time.
 */
export function resolveAiUsageRange(
  search: AiUsageSearch,
  now: Date = new Date(),
): ResolvedAiUsageRange {
  const today = todayYmd(now);
  const firstOfMonth = `${today.slice(0, 7)}-01`;
  const preset = search.range ?? DEFAULT_AI_USAGE_PRESET;

  switch (preset) {
    case 'last7d':
      return { preset, fromYmd: addDaysYmd(today, -6), toYmd: today };
    case 'last90d':
      return { preset, fromYmd: addDaysYmd(today, -89), toYmd: today };
    case 'this_month':
      return { preset, fromYmd: firstOfMonth, toYmd: today };
    case 'last_month': {
      const endOfLast = addDaysYmd(firstOfMonth, -1);
      return { preset, fromYmd: `${endOfLast.slice(0, 7)}-01`, toYmd: endOfLast };
    }
    case 'custom':
      // parseAiUsageSearch only keeps `custom` with both bounds in order, so
      // this branch cannot be reached with a half range through the URL. A
      // direct caller that does so falls back to the default span.
      if (search.from && search.to && search.from <= search.to) {
        return { preset, fromYmd: search.from, toYmd: search.to };
      }
      return { preset: 'last30d', fromYmd: addDaysYmd(today, -29), toYmd: today };
    case 'last30d':
    default:
      return { preset: 'last30d', fromYmd: addDaysYmd(today, -29), toYmd: today };
  }
}

// ---- day arithmetic ----------------------------------------------------------

function ymdToUtcMs(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

/** Inclusive day count between two calendar days (1 for the same day). */
export function dayCount(fromYmd: string, toYmd: string): number {
  return Math.floor((ymdToUtcMs(toYmd) - ymdToUtcMs(fromYmd)) / 86_400_000) + 1;
}

/** The Monday of the week a day falls in - Postgres `date_trunc('week')` starts Monday. */
export function mondayOf(ymd: string): string {
  const dow = new Date(ymdToUtcMs(ymd)).getUTCDay();
  return addDaysYmd(ymd, -((dow + 6) % 7));
}

/** How many buckets the RPC will return for this range at this grain. */
export function bucketCount(fromYmd: string, toYmd: string, grain: AiUsageGrain): number {
  if (grain === 'day') return dayCount(fromYmd, toYmd);
  if (grain === 'week') return Math.ceil(dayCount(mondayOf(fromYmd), toYmd) / 7);
  const [fy, fm] = fromYmd.split('-').map(Number);
  const [ty, tm] = toYmd.split('-').map(Number);
  return (ty - fy) * 12 + (tm - fm) + 1;
}

/**
 * The preceding equal-length period, in days: `[from - span, from)`. This mirrors
 * the RPC's own `previous_range` (contract §3) so the tile footers can name what
 * they compare against before any payload has landed.
 */
export function previousAiUsageRange(
  fromYmd: string,
  toYmd: string,
): { fromYmd: string; toYmd: string } {
  const span = dayCount(fromYmd, toYmd);
  return { fromYmd: addDaysYmd(fromYmd, -span), toYmd: addDaysYmd(fromYmd, -1) };
}

// ---- instants ----------------------------------------------------------------

/** The instant local midnight falls on for a `YYYY-MM-DD` day, as an ISO string. */
export function localMidnightIso(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
}

/**
 * The `p_from` / `p_to` pair for a day range. `to` is the start of the day AFTER
 * the last day, because the RPC's upper bound is EXCLUSIVE - passing the last
 * day's own midnight would drop that whole day.
 */
export function aiUsageInstants(
  range: { fromYmd: string; toYmd: string },
): { from: string; to: string } {
  return {
    from: localMidnightIso(range.fromYmd),
    to: localMidnightIso(addDaysYmd(range.toYmd, 1)),
  };
}

/** The browser's IANA zone, or UTC where Intl cannot say. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

// ---- captions ----------------------------------------------------------------

/**
 * "1 – 30 September 2026", and "29 September 2026" for a single day. The one-day
 * form is spelled out here because `formatDateRange` names a lone day in the
 * written form ("Tuesday, 29th September 2026"), which read as a different
 * format from the range beside it in the same sentence.
 */
export function aiUsageRangeLabel(fromYmd: string, toYmd: string): string {
  if (fromYmd === toYmd) return formatDateWrittenShort(`${fromYmd}T00:00:00`);
  return formatDateRange(`${fromYmd}T00:00:00`, `${toYmd}T00:00:00`);
}

/** The axis label for one bucket: "1 Sep" by day or week, "Sep 2026" by month. */
export function aiUsageBucketLabel(bucket: string, grain: AiUsageGrain): string {
  if (grain === 'month') return formatPeriodMonthYear(bucket.slice(0, 7));
  return formatDayMonth(`${bucket}T00:00:00`);
}

/** The tooltip / table form: the bucket named in full, with what kind of bucket it is. */
export function aiUsageBucketFullLabel(bucket: string, grain: AiUsageGrain): string {
  if (grain === 'month') return formatPeriodMonthYearLong(bucket.slice(0, 7));
  const day = formatDateShort(`${bucket}T00:00:00`);
  return grain === 'week' ? `Week of ${day}` : day;
}

/**
 * The generated line under the control bar. It states the range, the grain, the
 * zone the buckets were cut in and what the comparison is - never hand-written
 * prose that can drift from the parameters actually sent.
 */
export function aiUsageRangeSentence(
  range: ResolvedAiUsageRange,
  grain: AiUsageGrain,
  tz: string,
  previous: { fromYmd: string; toYmd: string } | null,
): string {
  const days = dayCount(range.fromYmd, range.toYmd);
  const parts = [
    aiUsageRangeLabel(range.fromYmd, range.toYmd),
    `${days} ${days === 1 ? 'day' : 'days'} by ${grain}`,
    tz,
  ];
  if (previous) {
    parts.push(`compared with ${aiUsageRangeLabel(previous.fromYmd, previous.toYmd)}`);
  }
  return `${parts.join(' · ')}.`;
}
